'use client';

import type { ReadonlyURLSearchParams } from 'next/navigation';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

const STORAGE_KEY = 'cjr:scrollPos';
const MAX_ENTRIES = 30;
// Absolute ceiling for how long we keep trying to restore. Many pages render a
// full-height loader while their data is fetched, so the document only grows
// tall enough to reach the saved position once that async data arrives. This
// window has to comfortably outlast a typical API response.
const RESTORE_TIMEOUT_MS = 8000;
// Position must hold across this many consecutive frames before we stop
// re-applying it. This is what defeats Safari's late scroll-to-top that fires
// after a client-side navigation settles.
const REQUIRED_STABLE_FRAMES = 3;
// The document height must be unchanged for this many consecutive frames before
// we treat async content as "finished growing" and jump to the saved position.
// Waiting for stability is what prevents the page from drifting/stepping as
// content loads in.
const HEIGHT_STABLE_FRAMES = 3;
// After the position has settled we briefly watch for late layout growth (e.g.
// images decoding) that could otherwise leave the user off-target.
const POST_RESTORE_WATCH_MS = 2000;
const POSITION_TOLERANCE_PX = 2;

// useLayoutEffect logs a warning when run during SSR. This component is
// client-only in practice, so fall back to useEffect on the server.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

type ScrollMap = Record<string, number>;

function now(): number {
  return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
}

// Build a stable storage key for a route. `searchParams.toString()` can emit
// params in different orders across visits (depending on how each navigation
// constructed the query), which would key the "same" page differently and make
// restoration miss. Sorting the params keeps the key stable.
function buildScrollKey(pathname: string, searchParams: ReadonlyURLSearchParams): string {
  const params = new URLSearchParams(searchParams.toString());
  params.sort();
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

// Read the current vertical scroll offset. `window.scrollY` is the modern API,
// but we fall back to `pageYOffset` and the scrolling element for older Safari.
function getScrollY(): number {
  if (typeof window === 'undefined') return 0;
  const fromWindow = window.scrollY ?? window.pageYOffset;
  if (typeof fromWindow === 'number' && !Number.isNaN(fromWindow)) return fromWindow;
  const el = document.scrollingElement || document.documentElement;
  return el ? el.scrollTop : 0;
}

// The maximum scrollable offset for the document.
function getMaxScrollable(): number {
  const doc = document.documentElement;
  const body = document.body;
  const scrollHeight = Math.max(doc ? doc.scrollHeight : 0, body ? body.scrollHeight : 0);
  return Math.max(0, scrollHeight - window.innerHeight);
}

// Jump to a vertical offset instantly. Safari can otherwise honor CSS
// `scroll-behavior` (animating the jump) or ignore `window.scrollTo` right
// after a navigation, so we force an instant jump and also set `scrollTop`
// directly as a belt-and-suspenders fallback.
function scrollToY(y: number): void {
  try {
    window.scrollTo({ top: y, left: 0, behavior: 'instant' as ScrollBehavior });
  } catch {
    window.scrollTo(0, y);
  }
  const el = document.scrollingElement || document.documentElement;
  if (el && Math.abs(el.scrollTop - y) > POSITION_TOLERANCE_PX) {
    el.scrollTop = y;
  }
}

function readMap(): ScrollMap {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ScrollMap) : {};
  } catch {
    return {};
  }
}

function writeMap(map: ScrollMap): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Ignore quota / serialization errors.
  }
}

function savePosition(key: string, y: number): void {
  const map = readMap();
  map[key] = y;

  // Cap the number of stored entries to avoid unbounded growth.
  const keys = Object.keys(map);
  if (keys.length > MAX_ENTRIES) {
    // Drop the oldest insertion-order entries.
    const excess = keys.slice(0, keys.length - MAX_ENTRIES);
    for (const k of excess) {
      delete map[k];
    }
  }

  writeMap(map);
}

function loadPosition(key: string): number | undefined {
  const map = readMap();
  return map[key];
}

export function ScrollRestorationManager() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const currentKey = buildScrollKey(pathname, searchParams);

  // Mirrors `currentKey` so the always-mounted scroll listener can persist under
  // the live key without re-subscribing on every navigation.
  const currentKeyRef = useRef(currentKey);
  currentKeyRef.current = currentKey;

  // `true` from navigation arrival until the restore for that page settles, is
  // aborted by the user, or times out. While set, the continuous-save listeners
  // must NOT write — otherwise the scroll-to-top that the browser/Next performs
  // on arrival would overwrite the saved position with 0 before we restore it.
  const isRestoringRef = useRef(false);
  // The pinned target for the in-flight restore. Captured before scroll-to-top
  // can corrupt storage, so the loop is immune to mid-restore overwrites.
  const restoreTargetRef = useRef<number | null>(null);
  // Teardown for whatever restore attempt is currently running (rAF loop,
  // listeners, observer). Lets us cleanly cancel before starting a new one.
  const teardownRestoreRef = useRef<(() => void) | null>(null);

  // Begin a "settle then jump" restore toward `targetY`. Content frequently
  // loads async (pages show a full-height loader first), so the document is not
  // tall enough to reach the target immediately. Rather than chase a moving
  // target every frame (which makes the page visibly step/drift downward as
  // content grows), we wait until the document height is both tall enough AND
  // stable, then jump once. After settling we briefly watch for late layout
  // growth and re-assert against Safari's late scroll-to-top.
  const beginRestore = useCallback((targetY: number) => {
    if (typeof window === 'undefined') return;

    // Cancel any previous in-flight restore before starting a new one.
    teardownRestoreRef.current?.();

    isRestoringRef.current = true;
    restoreTargetRef.current = targetY;

    const start = now();
    let aborted = false;
    let lastHeight = -1;
    let heightStableFrames = 0;
    let hasJumped = false;
    let settledFrames = 0;
    let rafId: number | null = null;
    let observer: ResizeObserver | null = null;
    let watchTimeout: ReturnType<typeof setTimeout> | null = null;

    const stop = () => {
      window.removeEventListener('wheel', abortRestore, userScrollOpts);
      window.removeEventListener('touchmove', abortRestore, userScrollOpts);
      window.removeEventListener('keydown', abortRestore);
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      if (watchTimeout != null) {
        clearTimeout(watchTimeout);
        watchTimeout = null;
      }
      isRestoringRef.current = false;
      restoreTargetRef.current = null;
      if (teardownRestoreRef.current === stop) {
        teardownRestoreRef.current = null;
      }
    };

    // If the user actively scrolls while we're still restoring, stop so we never
    // yank them back. Programmatic resets (e.g. Safari) do not fire these.
    function abortRestore() {
      aborted = true;
      stop();
    }
    const userScrollOpts = { passive: true } as AddEventListenerOptions;
    window.addEventListener('wheel', abortRestore, userScrollOpts);
    window.addEventListener('touchmove', abortRestore, userScrollOpts);
    window.addEventListener('keydown', abortRestore);

    // After the position settles, watch briefly for late layout growth and
    // re-apply the target if something shifts us away (without restarting the
    // whole loop on every resize).
    const startWatchPhase = () => {
      observer = new ResizeObserver(() => {
        if (aborted) return;
        const target = Math.min(restoreTargetRef.current ?? 0, getMaxScrollable());
        if (Math.abs(getScrollY() - target) > POSITION_TOLERANCE_PX) {
          scrollToY(target);
        }
      });
      observer.observe(document.documentElement);
      watchTimeout = setTimeout(stop, POST_RESTORE_WATCH_MS);
    };

    const tryRestore = () => {
      const target0 = restoreTargetRef.current ?? targetY;
      const timedOut = now() - start >= RESTORE_TIMEOUT_MS;

      const scrollHeight = Math.max(
        document.documentElement ? document.documentElement.scrollHeight : 0,
        document.body ? document.body.scrollHeight : 0
      );
      if (scrollHeight === lastHeight) {
        heightStableFrames += 1;
      } else {
        heightStableFrames = 0;
        lastHeight = scrollHeight;
      }

      const maxScrollable = getMaxScrollable();
      const tallEnough = maxScrollable >= target0;
      const heightSettled = heightStableFrames >= HEIGHT_STABLE_FRAMES;

      // Wait for the page to finish growing before the first jump, so the user
      // sees a single accurate jump instead of incremental drift.
      const readyToJump = (tallEnough && heightSettled) || timedOut;

      if (readyToJump) {
        const target = Math.min(target0, maxScrollable);
        if (Math.abs(getScrollY() - target) > POSITION_TOLERANCE_PX) {
          scrollToY(target);
          // A late reset (e.g. Safari) or a fresh layout shift moved us; reset
          // the settled counter so we keep enforcing.
          settledFrames = 0;
        } else {
          settledFrames += 1;
        }
        hasJumped = true;
      }

      if (aborted) return;

      const settled = hasJumped && settledFrames >= REQUIRED_STABLE_FRAMES;
      if (settled) {
        // Reached and held the target. Hand off to the brief watch phase.
        startWatchPhase();
        return;
      }
      if (timedOut) {
        stop();
        return;
      }
      rafId = window.requestAnimationFrame(tryRestore);
    };

    teardownRestoreRef.current = stop;
    rafId = window.requestAnimationFrame(tryRestore);
  }, []);

  // Disable native auto-restore once so the browser/Next don't fight us.
  useEffect(() => {
    if (typeof window === 'undefined' || !('scrollRestoration' in window.history)) {
      return;
    }
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  // Track current scrollY (throttled via requestAnimationFrame) and persist it
  // continuously under the live key. Saving as the user scrolls — rather than at
  // navigation time — means the leaving page's position is always stored
  // correctly, independent of when Next scrolls to top during a route change.
  // While a restore is in flight we skip saving so the arrival scroll-to-top
  // cannot overwrite the very position we are about to restore.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        if (!isRestoringRef.current) {
          savePosition(currentKeyRef.current, getScrollY());
        }
        ticking = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Persist scroll on page hide / tab switch (external redirects, tab close).
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const persist = () => {
      if (isRestoringRef.current) return;
      savePosition(currentKeyRef.current, getScrollY());
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        persist();
      }
    };

    window.addEventListener('pagehide', persist);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', persist);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // On arriving at a URL: snapshot its saved position BEFORE the browser/Next
  // scroll-to-top can run (useLayoutEffect fires before paint), arm the restore
  // guard, then start the restore. The guard blocks the save listeners so the
  // saved position cannot be overwritten with 0 during the navigation.
  useIsomorphicLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    const savedY = loadPosition(currentKey);
    if (savedY != null && savedY > 0) {
      beginRestore(savedY);
    } else {
      // Nothing to restore: make sure the guard is down so this fresh page can
      // start tracking scroll immediately.
      teardownRestoreRef.current?.();
      isRestoringRef.current = false;
      restoreTargetRef.current = null;
    }

    return () => {
      teardownRestoreRef.current?.();
    };
  }, [currentKey, beginRestore]);

  // Back/forward via the bfcache (common on Safari) can restore a page without a
  // React remount, so the layout effect above won't re-run. Re-arm restore on
  // `pageshow` with `persisted` set.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      const savedY = loadPosition(currentKeyRef.current);
      if (savedY != null && savedY > 0) {
        beginRestore(savedY);
      }
    };

    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [beginRestore]);

  return null;
}

export default ScrollRestorationManager;
